import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import ApiSectionErrorBoundary from "./ApiSectionErrorBoundary";

const PlumeCanvas = lazy(() => import("./PlumeCanvas"));

function PlumeCanvasLoader() {
  return (
    <div className="flex h-[500px] w-full items-center justify-center rounded-lg border border-border bg-canvas-subtle md:h-[600px]" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-2 text-xs text-fg-secondary">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden />
        <span>Loading plume model…</span>
      </div>
    </div>
  );
}

/**
 * Responsive thermal plume card: mobile ~full width × 500px, desktop 800×600 drawing buffer.
 */
export default function PlumeThermalSection({ plume }) {
  const wrapRef = useRef(null);
  const [[w, h], setDims] = useState([800, 500]);
  const [ts, setTs] = useState(() => new Date().toISOString());

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const desktop = window.matchMedia("(min-width: 768px)").matches;
      const cw = Math.max(320, Math.floor(el.getBoundingClientRect().width || 800));
      if (desktop) setDims([800, 600]);
      else setDims([Math.min(800, cw), 500]);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    const mq = window.matchMedia("(min-width: 768px)");
    mq.addEventListener("change", apply);
    return () => {
      ro.disconnect();
      mq.removeEventListener("change", apply);
    };
  }, []);

  useEffect(() => {
    setTs(new Date().toISOString());
  }, [plume.powerMW, plume.dischargeTemp, plume.currentSpeed, plume.ambientTemp, plume.depth]);

  return (
    <ApiSectionErrorBoundary>
      <div ref={wrapRef} className="card p-6">
        <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-accent">Thermal Plume Model</div>
        <div className="w-full overflow-hidden rounded-lg border border-border">
          <Suspense fallback={<PlumeCanvasLoader />}>
            <PlumeCanvas {...plume} pixelWidth={w} pixelHeight={h} />
          </Suspense>
        </div>
        <p className="mt-2 text-xs text-fg-secondary">
          Responsive visualization — Last updated: {ts}
        </p>
      </div>
    </ApiSectionErrorBoundary>
  );
}
